import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Delete } from "lucide-react";
import { getDeviceToken } from "./TabletEntry";

export default function TabletPin() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh: refreshAuth } = useAuth();
  const [operaio, setOperaio] = useState(location.state?.operaio || null);
  const [session, setSession] = useState(location.state?.session || null);
  const [loading, setLoading] = useState(!operaio);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showFirstLogin, setShowFirstLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");

  const dt = getDeviceToken();
  const PIN_LEN = 4;

  useEffect(() => {
    if (!dt) { navigate("/tablet"); return; }
    if (!operaio) {
      // Reload session from server if we came directly to /tablet/pin/:id
      (async () => {
        try {
          const r = await api.get(`/tablet/session?device_token=${encodeURIComponent(dt)}`);
          setSession(r.data);
          const op = (r.data.operai || []).find(o => o.user_id === userId);
          if (!op) { toast.error("Utente non trovato sul furgone"); navigate("/tablet/home"); return; }
          setOperaio(op);
          setEmail(op.email || "");
          if (!op.has_pin) setShowFirstLogin(true);
        } catch (e) {
          toast.error(formatErr(e));
          navigate("/tablet");
        } finally { setLoading(false); }
      })();
    } else {
      setEmail(operaio.email || "");
      if (!operaio.has_pin) setShowFirstLogin(true);
    }
    // eslint-disable-next-line
  }, []);

  const submitPin = async (thePin) => {
    setBusy(true);
    try {
      await api.post("/tablet/pin-login", { device_token: dt, user_id: userId, pin: thePin });
      await refreshAuth();
      // Redirect based on role: operaio → jobs, personale/admin → dashboard
      navigate(operaio?.role === "operaio" ? "/lavori" : "/");
    } catch (e) {
      setPin("");
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const pressDigit = (d) => {
    if (busy || pin.length >= PIN_LEN) return;
    const next = pin + d;
    setPin(next);
    if (next.length === PIN_LEN) submitPin(next);
  };
  const backspace = () => setPin(p => p.slice(0, -1));

  const submitFirstLogin = async () => {
    if (!email || !password) { toast.error("Inserisci email e password"); return; }
    if (newPin.length !== PIN_LEN || newPin !== newPin2) { toast.error("I due PIN devono coincidere (4 cifre)"); return; }
    if (!/^\d{4}$/.test(newPin)) { toast.error("Il PIN deve essere di 4 cifre"); return; }
    setBusy(true);
    try {
      await api.post("/tablet/first-login", { device_token: dt, email, password, pin: newPin });
      await refreshAuth();
      toast.success("PIN salvato. Bentornato!");
      navigate(operaio?.role === "operaio" ? "/lavori" : "/");
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  if (loading || !operaio) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-950 flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-white animate-spin"/>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white flex flex-col">
      <header className="p-4 flex items-center gap-3 safe-top safe-x">
        <button onClick={() => navigate("/tablet/home")} className="p-2 rounded-lg hover:bg-white/10" data-testid="tablet-pin-back">
          <ArrowLeft className="w-5 h-5"/>
        </button>
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-200">{session?.furgone_name}</div>
          <div className="heading text-lg font-bold">Ciao {operaio.name.split(" ")[0]}!</div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        {showFirstLogin ? (
          <div className="w-full max-w-md">
            <div className="bg-white/10 backdrop-blur border border-white/10 rounded-2xl p-6" data-testid="tablet-first-login">
              <h2 className="heading text-2xl font-bold mb-1">Primo accesso</h2>
              <p className="text-blue-200 text-sm mb-4">Verifica la tua identità con email e password, poi scegli un PIN a 4 cifre da usare da qui in avanti.</p>
              <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Email</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                     className="w-full bg-slate-900/60 border border-white/20 rounded-lg px-3 py-3 text-white outline-none focus:border-blue-400" data-testid="tablet-fl-email"/>
              <label className="text-[10px] uppercase tracking-widest text-blue-200 mt-3 mb-1 block">Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
                     className="w-full bg-slate-900/60 border border-white/20 rounded-lg px-3 py-3 text-white outline-none focus:border-blue-400" data-testid="tablet-fl-password"/>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Nuovo PIN (4 cifre)</label>
                  <input type="tel" inputMode="numeric" maxLength={4} value={newPin} onChange={e=>setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                         className="w-full text-center text-2xl tracking-[0.4em] font-bold bg-slate-900/60 border border-white/20 rounded-lg py-3 outline-none focus:border-blue-400" data-testid="tablet-fl-pin1"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Conferma PIN</label>
                  <input type="tel" inputMode="numeric" maxLength={4} value={newPin2} onChange={e=>setNewPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
                         className="w-full text-center text-2xl tracking-[0.4em] font-bold bg-slate-900/60 border border-white/20 rounded-lg py-3 outline-none focus:border-blue-400" data-testid="tablet-fl-pin2"/>
                </div>
              </div>
              <button onClick={submitFirstLogin} disabled={busy}
                      className="mt-5 w-full bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2"
                      data-testid="tablet-fl-submit">
                {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : "Salva PIN e accedi"}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm text-center">
            <h2 className="heading text-2xl font-bold mb-6">Inserisci il PIN</h2>
            <div className="flex justify-center gap-3 mb-8" data-testid="tablet-pin-dots">
              {Array.from({ length: PIN_LEN }).map((_, i) => (
                <div key={i} className={`w-4 h-4 rounded-full border-2 border-white/50 ${i < pin.length ? "bg-white" : "bg-transparent"}`}/>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="tablet-pin-keypad">
              {[1,2,3,4,5,6,7,8,9].map(n => (
                <button key={n} disabled={busy} onClick={() => pressDigit(String(n))}
                        className="text-3xl font-bold py-6 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/10 transition active:scale-95 disabled:opacity-40"
                        data-testid={`tablet-pin-${n}`}>
                  {n}
                </button>
              ))}
              <button disabled={busy} onClick={() => setShowFirstLogin(true)}
                      className="text-xs font-semibold py-6 rounded-2xl bg-transparent hover:bg-white/10 text-blue-200"
                      data-testid="tablet-pin-forgot">
                PIN dimenticato?
              </button>
              <button disabled={busy} onClick={() => pressDigit("0")}
                      className="text-3xl font-bold py-6 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/10 transition active:scale-95 disabled:opacity-40"
                      data-testid="tablet-pin-0">
                0
              </button>
              <button disabled={busy || pin.length === 0} onClick={backspace}
                      className="flex items-center justify-center py-6 rounded-2xl bg-white/5 hover:bg-white/15 border border-white/10 transition disabled:opacity-40"
                      data-testid="tablet-pin-back-key">
                <Delete className="w-6 h-6"/>
              </button>
            </div>
            {busy && <div className="mt-6 inline-flex items-center gap-2 text-blue-200 text-sm"><Loader2 className="w-4 h-4 animate-spin"/> Verifica...</div>}
          </div>
        )}
      </main>
    </div>
  );
}
